import React, { useEffect, useRef } from 'react';
import { Home, Building2, Heart, FileText } from 'lucide-react';

const ServicesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, index) => {
              setTimeout(() => { el.classList.add('visible'); }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Home,
      title: 'Home Collection',
      description: 'For individuals, families and anyone who prefers the comfort and privacy of home.',
      image: 'https://images.pexels.com/photos/6753272/pexels-photo-6753272.jpeg?auto=compress&cs=tinysrgb&w=940',
    },
    {
      icon: Building2,
      title: 'Workplace & Corporate',
      description: 'Scheduled visits to offices for employee health checks and corporate wellness programs.',
      image: 'https://images.pexels.com/photos/5327586/pexels-photo-5327586.jpeg?auto=compress&cs=tinysrgb&w=940',
    },
    {
      icon: Heart,
      title: 'Aged Care Facilities',
      description: 'Regular weekly collection rounds for residential aged care and retirement villages.',
      image: 'https://images.unsplash.com/photo-1765896387387-0538bc9f997e?w=940',
    },
    {
      icon: FileText,
      title: 'GP-Referred Patients',
      description: 'We accept all pathology request forms. Your GP refers, we collect.',
      image: 'https://images.unsplash.com/photo-1758691462123-8a17ae95d203?w=940',
    },
  ];

  return (
    /* Band 2: pure white — alternates with off-white How It Works above */
    <section
      id="services"
      ref={sectionRef}
      className="py-20 md:py-28"
      style={{ background: '#FFFFFF' }}
    >
      <div className="container">
        <div className="text-center mb-14 fade-up">
          <span className="pill-label">Our Services</span>
          <h2 className="mt-2 mb-4 text-[#00001E]">Who we collect for</h2>
          <p className="text-[#2B3879] text-lg max-w-2xl mx-auto">
            Professional pathology collection services for every setting and need.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-7 max-w-6xl mx-auto">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div key={service.title} className="fade-up">
                <div
                  className="group relative rounded-2xl overflow-hidden h-80 cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  style={{ boxShadow: '0 6px 28px rgba(0,0,0,0.12)' }}
                >
                  {/* Photo with dark overlay — intentionally dark for legibility */}
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{
                      backgroundImage: `linear-gradient(rgba(0,0,30,0.58), rgba(0,0,30,0.52)), url(${service.image})`,
                    }}
                  />

                  {/* Glass label at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div
                      className="backdrop-blur-md rounded-xl p-5"
                      style={{
                        background: 'rgba(0,0,30,0.55)',
                        border: '1px solid rgba(128,229,203,0.25)',
                      }}
                    >
                      <div className="flex items-start gap-3 mb-2">
                        <div
                          className="p-2 rounded-lg flex-shrink-0"
                          style={{ background: 'rgba(128,229,203,0.20)' }}
                        >
                          <Icon className="w-5 h-5" style={{ color: '#80E5CB' }} />
                        </div>
                        <h3
                          className="font-semibold text-white"
                          style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: '1.05rem' }}
                        >
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>
                        {service.description}
                      </p>
                    </div>
                  </div>

                  {/* Mint hover tint */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#80E5CB] to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-300" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
